import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { getAuth, signInWithCustomToken, onIdTokenChanged } from 'firebase/auth';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../domain/Firebase';
import GlobalContext from '../domain/GlobalContext';
import { api } from '../domain/ApiService';
import '../theme/pages.css';
import '../theme/chat.css';
import { IonItem, IonAvatar, IonLabel, IonIcon, IonSpinner, IonButton, IonModal, IonTextarea, IonSelect, IonSelectOption, IonToggle } from '@ionic/react';
import { arrowUpCircle, attach, book, bookmark, bookmarks, clipboard, clipboardOutline, closeCircle, fileTrayFull, informationCircle } from 'ionicons/icons';
import { useLocation } from 'react-router';
import { appConfig } from '../domain/Config';
import useAuth from '../domain/Auth';
import ReactMarkdown from 'react-markdown';
import Prompts from './Prompts';
import { Prompt, Assistant, Initiative } from '../domain/Types';
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

interface Message {
  id: string;
  text: string;
  context?: string;
  sender: string;
  timestamp: any;
}

type ChatProps<T> = {
  assistant: Assistant;
  defaultAssistantId: string;
  defaultMessage: string | '';
  onAssistantMessage?: (message: string | null) => void;
  addToKnowledgeBase?: (item: string | null, target: string | null) => void;
};

const Chat = <T extends { [key: string]: any }>({
   assistant, defaultAssistantId, onAssistantMessage, addToKnowledgeBase, defaultMessage = ''
  }: ChatProps<T>) => {

  useEffect(() => {
    setNewMessage("");
    if (defaultMessage) {
      setIsExpanded(true);
    }
    setNewMessage(defaultMessage.trim());
  }, [defaultMessage]);
  
  const { token, isAuthenticated, logout } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);

  const [selectedMessageContent, setSelectedMessageContent] = useState<string | null>(null);
  const [selectedMessageTarget, setSelectedMessageTarget] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [messageContext, setMessageContext] = useState<String | null>(null);
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState<boolean>(false);
  const [showFileAttachmentModal, setShowFileAttachmentModal] = useState<boolean>(false);
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const [forcedKnowledge, setForcedKnowledge] = useState(true);
  const handleToggleChange = (e: CustomEvent) => {
    setForcedKnowledge(e.detail.checked);
  };
  const [newMessage, setNewMessage] = useState(defaultMessage);
  const [showTypingSpinner, setShowTypingSpinner] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { user, users, initiative, setInitiative, setInitiatives } = useContext(GlobalContext);
  const [ newMessageFile, setNewMessageFile ] = useState<string | null>(null);
  const [ newMessageFileURL, setNewMessageFileURL ] = useState<string | null>(null);
  const [ isFileLoaded, setIsFileLoaded ] = useState(true);
  const location = useLocation();

  const uploadFile = async (file: File) => {
    try {

      if (!user) {
        console.error('Upload failed: No user.');
        return;
      }

      if (file.name) {
        
        setIsFileLoaded(false);
        setNewMessageFile(file.name);
        const storage = getStorage();
        const filePath = 'messages/'+ user?.instance_id +'/' + file.name
        const storageRef = ref(storage, filePath); 
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        setNewMessageFileURL(downloadURL);
      }
      else {

        console.error('Knowledge base item missing');
      }

      setIsFileLoaded(true);

      // You can store this URL in Firestore or use it as needed
    } catch (error) {
      console.error('Upload failed', error);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };
   
  const fetchThreadSummaries = useCallback(async (chatMessages: Message[]) => {
  
    try {

    } catch (err) {
      console.error('Error fetching:', err);
    }
  
  }, [initiative, messages, assistant]);

  useEffect(() => {

    scrollToBottom();


    if (messages != null && messages.length == 0) {

      // const initialMessage = { 
      //   id: "0",
      //   text: "Hello, how can I help you today?",
      //   sender: "assistant",
      //   chatroom_id: assistant.uid,
      //   timestamp: serverTimestamp()
      // };

      // setMessages([ initialMessage ]);
    }
    
  }, [location, messages]);

  useEffect(() => {
    const authenticateWithFirebase = async () => {
      if (!isAuthenticated || !token) return;

      try {

        const auth = getAuth();
        signInWithCustomToken(auth, token)
          .then((userCredential) => {

            onIdTokenChanged(auth, async (user) => {
                if (user) {
                  const decodedToken = await user.getIdTokenResult();
                  const claims = decodedToken.claims;
                }
              });
          })
          .catch((error) => {
            console.error('Error with Firebase signInWithCustomToken: ', error);
          });        
      } catch (error) {
        console.error('Error with Firebase authentication: ', error);
      }
    };

    if (user?.type == "Auth0")
      authenticateWithFirebase();

  }, [isAuthenticated, api]);

  useEffect(() => {

    if (assistant != null) {

      const messagesQuery = query(
        collection(firestore, 'messages'),
        where('chatroom_id', '==', assistant.uid),
        orderBy('timestamp')
      );

      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        const fetchedMessages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as Message);
        
        setMessages(fetchedMessages);

        if (appConfig.initiativeThreadSummaries)
          fetchThreadSummaries(fetchedMessages);
      });

      const exp = () => unsubscribe();
      // fetchThreadSummaries();      
      return exp;
    }    
  }, [assistant]);

  const apiCallWithTimeout = async (jsonInput: any) => {
    // Timeout promise that resolves after 30 seconds
    const timeoutPromise = new Promise<any>((resolve) => {
      setTimeout(() => {
        resolve({ "response": "Could not connect to OpenAI API", "thread_id": null });
      }, 90000);
    });
  
    // Your API call wrapped in a promise, directly using the jsonInput
    const apiCallPromise = api('/assistant', jsonInput, initiative) as Promise<any>;
  
    // Wait for whichever promise resolves first
    return Promise.race([apiCallPromise, timeoutPromise]);
  };

  const sendMessage = async () => {

    setShowTypingSpinner(true);

    if (user === null || user?.uid == null) {
      await new Promise(resolve => setTimeout(resolve, 5000));
    }

    if (user === null || user?.uid == null) {
      console.error('User not authenticated '+ JSON.stringify(user));
      setNewMessage('');
      setNewMessageFile(null);
      setNewMessageFileURL(null);
      setShowTypingSpinner(false);
      setIsExpanded(false);
      return;      
    }

    if (!newMessage.trim()) return;

    let docFileURL = null;
    let docMessage = ""+ newMessage

    if (newMessageFile) {

      docMessage = "_Attachment: "+ newMessageFile + "_\n\n" + newMessage;
      docFileURL =  ""+ newMessageFileURL;
    }

    setNewMessage("");
    setNewMessageFile(null);
    setNewMessageFileURL(null);
    setIsExpanded(false);
  
    await addDoc(collection(firestore, 'messages'), {
      text: docMessage,
      sender: user?.uid,
      chatroom_id: assistant.uid,
      timestamp: serverTimestamp()
    });  

    const response = await apiCallWithTimeout({
        "message": docMessage,
        "message_file": docFileURL,
        "assistant_id": defaultAssistantId,
        "thread_id": appConfig.initiativeThread ? null : assistant.thread_id,
        "initiative_thread": appConfig.initiativeThread,
        "room": assistant.uid,
        "forced_knowledge": forcedKnowledge,
      }) as any;

    if (!assistant.thread_id || response.thread_id !== assistant.thread_id) {

        if(!appConfig.initiativeThread) {

          assistant.thread_id = response.thread_id;

          if (initiative)
            await api("/initiative/update", initiative, initiative); 
        }
        else {

            setInitiative(await api("/initiative/get", {}, initiative)); 
            setInitiatives(await api("/initiative/list", {}, initiative)); 
        }
    }

    setShowTypingSpinner(false);

    await addDoc(collection(firestore, 'messages'), {
        text: response.response,
        context: response.context,
        sender: "assistant",
        chatroom_id: assistant.uid,
        timestamp: serverTimestamp()
    });

    if (onAssistantMessage) {

      onAssistantMessage(response.response);
      window.dispatchEvent(new Event('assistantMessage'));
    }
  };
  
  const adjustHeight = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto'; // Reset the height
    element.style.height = `${element.scrollHeight}px`; // Set the new height
  
    // Limit the number of rows to 10
    const lineHeight = parseInt(window.getComputedStyle(element).lineHeight);
    const maxRowsHeight = lineHeight * 10;
    if (element.scrollHeight > maxRowsHeight) {
      element.style.overflowY = 'scroll';
      element.style.height = `${maxRowsHeight}px`;
    } else {
      element.style.overflowY = 'hidden';
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    adjustHeight(e.target);
  };

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {

    setNewMessage(event.target.value);
    if (event.target.value.length > 150 ||  event.target.value.includes('\n')) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  };
  
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = event.clipboardData.getData('Text');
    if (pastedText.length > 150 || pastedText.includes('\n')) {
      setIsExpanded(true);
    }
    else {
      
      setIsExpanded(false);
    }
  }; 
  
  const handleKeyDown = (event: { key: string; shiftKey: any; preventDefault: () => void; }) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
    else if (newMessage.length > 150 || newMessage.includes('\n')) {

      setIsExpanded(true);
    }
    else {

      setIsExpanded(false);
    }
  };

  const scrollToBottom = () => {
    const chatContainer = document.querySelector('.chat-container');
  
    if (chatContainer) {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };  

  const handlePromptClick = (item: Prompt | null) => {

    if (item)
      setNewMessage(item.content);   

    setShowPromptsModal(false);
  }

  const updateInitiative = async () => {

    if (initiative != null) {

      try { 

        const response = await api("/initiative/update", initiative, initiative) as Initiative; 
        window.dispatchEvent(new Event('initiativeUpdated'));
      }
      catch (err) { console.error('Error:', err); }
    }
  }

  const openFileAttachmentModal = () => {
    setShowFileAttachmentModal(true);
  };

  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const copyToClipboard = (text: string, messageId: string) => {
    if (navigator.clipboard) { // Modern async clipboard API
      navigator.clipboard.writeText(text).then(() => {
        // Optionally, show a notification or change the icon to indicate success
      }).catch(err => {
        console.error('Failed to copy text: ', err);
        // Optionally, handle errors (e.g., clipboard permissions denied)
      });
    } else {
      // Fallback for older browsers (not recommended for production use)
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopiedMessageId(messageId);
    setTimeout(() => {
      setCopiedMessageId(null);
    }, 1000); 
  };

  return (
    <>
    <div className='chat-container'>
      {messages.length > 0 ? (
        messages.map(message => (
          <>
            <IonItem color="none" lines="none">
              <IonAvatar className="chat-avatar">
                  {message.sender == 'assistant' ? (
                    <img alt="Assistant" src="/assets/icon/favicon.png" />
                  ) : (
                      <img alt="" src={user != null && user.avatar != null ? user.avatar : "/assets/avatar.jpg" } />
                  )}
              </IonAvatar>
              <IonLabel>
                {(() => {                    
                  const found_user = users?.find(u => u.auth_id === message.sender);
                  const senderName = message.sender === 'assistant' ? 'Assistant' : (user?.auth_id === message.sender ? 'You' : found_user?.name);
                  return <div className="chat-avatar-label">{senderName}</div>;
                })()}
              </IonLabel>
            </IonItem>
            <div className='chat-message'>
              <ReactMarkdown className="reactMarkdown">
                {message.text}
              </ReactMarkdown>        
              <div className='chat-message-actions'>
                {/* icon={copiedMessageId === message.id ? clipboard : clipboardOutline}  */}
              <IonButton 
                  id={message.id}
                  size='small' 
                  onClick={() => copyToClipboard(message.text, message.id)}>Copy</IonButton>
                <IonButton onClick={() => { setSelectedMessage(message); setSelectedMessageContent(message.text); setShowKnowledgeBaseModal(true); }} size='small'>Add to knowledge base</IonButton>
                {(user && (user.role === 'admin' || user.role === 'superadmin') && typeof message.context === 'string' && message.context) ? (
                <IonIcon onClick={() => {

                        try {

                            if(message.context) {

                              // Parse the context to access its properties
                              const contextObject = JSON.parse(message.context);
                              if (contextObject) {
                                  const knowledgeObject = JSON.parse(contextObject.knowledge)

                                  if (knowledgeObject) {

                                    // Map over the array of JSON strings
                                    const contents = knowledgeObject.map((k: string) => { // Explicitly declare k as string
                                        const fixedJson = k.replace(/'/g, '"'); // Replace single quotes with double quotes
                                        try {
                                            const json = JSON.parse(fixedJson); // Parse the corrected JSON string
                                            return json.content; // Extract the 'content' field
                                        } catch (error) {
                                            console.error('Error parsing knowledge item:', error);
                                            return 'Invalid knowledge format'; // Provide a fallback message
                                        }
                                    });
                                    
                                    if (contents.length > 0)
                                      setMessageContext("Knowledge: "+ contents.join('\n\n'));
                                    else
                                      setMessageContext("Knowledge context: "+ JSON.stringify(contextObject));
                                }
                                else {

                                  setMessageContext("Context: "+ JSON.stringify(contextObject));
                                }
                              } else {
                                  setMessageContext('No related knowledge base data');
                              }
                            }
                        } catch (error) {

                            if (message.context)
                              setMessageContext("Raw context: "+ message.context);
                            else
                              setMessageContext('No related knowledge base data');
                          }
                    
                }} icon={informationCircle} size='small' className="link chat-message-action-icon" />
                ) : (<></>)}
              </div>
            </div>
          </>
        ))
      ) : (
        <div className="centered-content" style={{minHeight: "100hv", maxWidth: "650px", verticalAlign: "center", paddingTop: "0px" }}>
          {/* <IonAvatar className="chat-avatar">
            <img alt="Assistant" src="/assets/icon/favicon.png" />
          </IonAvatar>
            &nbsp;&nbsp;&nbsp;How can I help you today? */}
        </div>
      )}
      {showTypingSpinner ? (
        <>
          <IonItem color="none" lines="none">
          <IonAvatar className="chat-avatar">
            <img alt="Assistant" src="/assets/icon/favicon.png" />
          </IonAvatar>
          <IonLabel>
            <div className="chat-avatar-label">Assistant</div>
          </IonLabel>
          </IonItem>
          <div className='chat-message' key="assistant_typing">
            <IonSpinner name="dots" color="dark" style={{marginLeft: "3px"}}></IonSpinner>
          </div>
        </>
      ) : (<></>)}
    </div>
    TEST
    <div className={`chat-input-container ${isExpanded ? 'expanded-chat' : ''}`}>
      <input type="file" style={{display: 'none'}} onChange={onFileChange} ref={inputRef} />
      {!newMessageFile ? (
        <IonIcon 
          icon={ attach } 
          size='large' 
          className="attach-button" 
          style={showTypingSpinner ? { opacity: "0.5" } : {}} 
          onClick={showTypingSpinner ? () => {} : () => inputRef.current?.click() } />
        ) : (
        <IonIcon 
          icon={ fileTrayFull } 
          size='large' 
          className="attach-button" 
          style={showTypingSpinner ? { opacity: "0.5" } : {}} 
          onClick={() => openFileAttachmentModal() } />
        )}
      <textarea
        className={`chat-input ${isExpanded ? 'expanded-chat' : ''}`}
        value={newMessage}
        onChange={handleInput}
        placeholder={showTypingSpinner ? "" : "Message assistant ..."}
        rows={1}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}          
        disabled={showTypingSpinner}
      />
      <IonIcon 
          icon={arrowUpCircle}
          size='large' 
          className="send-button"
          style={showTypingSpinner ? { opacity: "0.5" } : {}} 
          onClick={showTypingSpinner ? undefined : sendMessage } />      
      <IonIcon 
          icon={bookmarks}
          size='large' 
          className="prompts-button"
          style={showTypingSpinner ? { opacity: "0.5" } : {}} 
          onClick={showTypingSpinner ? () => {} : () => setShowPromptsModal(true) } />
    </div>
    <div className={`chat-toggle-container`}>
      <IonToggle labelPlacement="end" 
              onIonChange={handleToggleChange}
              checked={forcedKnowledge} className={`chat-toggle`}>
          Forced knowledge search
      </IonToggle>
    </div>
    <IonModal className='form-modal' isOpen={showFileAttachmentModal} onDidDismiss={() => setShowFileAttachmentModal(false) }>
          <div className='form'>
            <div style={{paddingBottom: "20px" }}>
            Attachment: {newMessageFile}
            </div>
            <IonButton onClick={() => { setNewMessageFile(null); setNewMessageFileURL(null); setShowFileAttachmentModal(false); }}>Remove attachment</IonButton>
            <IonButton className='basic' onClick={() => { setShowFileAttachmentModal(false); }}>Back to message</IonButton>
          </div>
    </IonModal>

    <IonModal className='form-modal' isOpen={showKnowledgeBaseModal} onDidDismiss={() => setShowKnowledgeBaseModal(false)}>
          <div className='form'>

          <IonTextarea 
            className='form-element'
            label="Content"
            labelPlacement="floating"
            fill="outline"
            rows={5}            
            value={selectedMessageContent}            
            onIonChange={e => setSelectedMessageContent(prev => {
              if (prev && e.detail.value !== null && e.detail.value !== undefined) {
                  prev = e.detail.value;
              }
              return prev;
            })} 
            placeholder="Content"
          />

          <IonSelect
              label="Knowledge base"
              placeholder="Knowledge base"
              labelPlacement="floating"
              value={selectedMessageTarget}
              interface="popover"
              onIonChange={e => setSelectedMessageTarget(e.detail.value)}
            >
              <IonSelectOption key="initiative" value="initiative">Client</IonSelectOption>
              <IonSelectOption key="folder" value="folder">Folder</IonSelectOption>
              <IonSelectOption key="assistant" value="assistant">Chat room</IonSelectOption>
          </IonSelect>

          <IonButton onClick={() => { if(addToKnowledgeBase && selectedMessageContent) addToKnowledgeBase(selectedMessageContent, selectedMessageTarget); setShowKnowledgeBaseModal(false); }}>Add to knowledge base</IonButton>
          <IonButton className='basic' onClick={() => { setShowKnowledgeBaseModal(false); }}>Cancel</IonButton>

          </div>
      </IonModal>

      <IonModal className='form-modal' isOpen={showPromptsModal} onDidDismiss={() => setShowPromptsModal(false)}>

        <div className='form' style={{height: "80vh", maxHeight: "500px"}}>
                
          <IonIcon icon={closeCircle} size='small' className='link' slot='end' onClick={() => { setShowPromptsModal(false); }} />

          <Prompts 
            showHeader={false} 
            showActions={false} 
            showSearch={true}             
            handleClick={handlePromptClick} />

        </div>

      </IonModal>

      <IonModal className='form-modal' isOpen={messageContext != null} onDidDismiss={() => setMessageContext(null)}>


        <div className='form' style={{height: "80vh", maxHeight: "500px"}}>                
              <pre style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                height: '80vh',
                maxHeight: '500px'
              }}>{messageContext}</pre>
        </div>

      </IonModal>
      </>
  );
};

export default Chat;